import { useState, useEffect, useMemo, type RefObject } from 'react';

import type { GroupedCategory } from 'src/utils/groupMenuItems';
import { scrollToElement } from 'src/utils/scrollToElement';
import { useScrollSpy } from 'src/hooks/useScrollSpy';
import { NAV_ROW_HEIGHT, CONTENT_ANCHOR_ID } from 'src/constants/layout';

export const useMenuNavigation = (
  grouped: GroupedCategory[],
  heroRef: RefObject<HTMLDivElement | null>,
) => {
  const hasSubcategories = useMemo(
    () => grouped.some((g) => g.subgroups.length > 0),
    [grouped],
  );

  const firstTopId = grouped[0]?.category.id ?? '';

  const { activeId: scrollActiveTopId, onInView: onTopInView } =
    useScrollSpy(firstTopId);
  const [activeTopCategoryId, setActiveTopCategoryId] = useState(firstTopId);

  const activeGroup = useMemo(
    () =>
      grouped.find((g) => g.category.id === activeTopCategoryId) ?? grouped[0],
    [grouped, activeTopCategoryId],
  );

  const firstSubId = activeGroup?.subgroups[0]?.category.id ?? '';
  const { activeId: activeSubId, onInView: onSubInView } =
    useScrollSpy(firstSubId);

  useEffect(() => {
    if (firstSubId) onSubInView(firstSubId, true);
  }, [activeTopCategoryId, firstSubId, onSubInView]);

  const scrollToContentIfNeeded = (group: GroupedCategory) => {
    const el = document.getElementById(CONTENT_ANCHOR_ID);
    if (!el) return;

    const offset =
      group.subgroups.length > 0 ? NAV_ROW_HEIGHT * 2 : NAV_ROW_HEIGHT;
    const target = el.getBoundingClientRect().top + window.scrollY - offset;
    const heroHeight = heroRef.current?.offsetHeight ?? 0;

    const alreadyShowingIntro = window.scrollY < target && heroHeight === 0;
    if (alreadyShowingIntro) return;

    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  const handleSelectTopCategory = (id: string) => {
    if (!hasSubcategories) {
      scrollToElement(id);
      return;
    }

    setActiveTopCategoryId(id);

    const nextGroup = grouped.find((g) => g.category.id === id);
    if (!nextGroup) return;

    requestAnimationFrame(() => scrollToContentIfNeeded(nextGroup));
  };

  const handleSelectSubcategory = (id: string) => {
    scrollToElement(id, NAV_ROW_HEIGHT * 2);
  };

  const topActiveId = hasSubcategories
    ? (activeGroup?.category.id ?? '')
    : scrollActiveTopId;

  return {
    activeGroup,
    hasSubcategories,
    topActiveId,
    activeSubId,
    onTopInView,
    onSubInView,
    handleSelectTopCategory,
    handleSelectSubcategory,
  };
};
